/**
 * AgentChatWidget — the site agent (agent.inferrex.com), embedded as a
 * site-wide floating chat on the marketing site.
 *
 * Talks A2A (JSON-RPC message/send) to the same worker other agents use — one
 * agent, two doors. Answers come only from published site content; the corpus
 * lookup and product answers are deterministic. No cookies, no identity: the
 * conversation lives in component state and dies with the tab.
 *
 * Sits bottom-right ABOVE the MarketingCtaPopup slot (bottom-4): the launcher
 * anchors at bottom-20 so the two never collide.
 */

import { useEffect, useRef, useState } from 'react';
import { Sparkles, X, SendHorizonal } from 'lucide-react';
import { cn } from '../utils';

const AGENT_URL = 'https://agent.inferrex.com/';

interface ChatMessage {
  readonly role: 'user' | 'agent';
  readonly text: string;
}

const SUGGESTIONS = [
  'How does self-healing work?',
  'Is Salesforce in the corpus?',
  'What does Inferrex cost?',
];

/** Render agent text with bare URLs made clickable — nothing else is markup. */
function Linkified({ text }: { readonly text: string }) {
  const parts = text.split(/(https?:\/\/[^\s]+)/g);
  return (
    <>
      {parts.map((p, i) =>
        /^https?:\/\//.test(p) ? (
          <a key={i} href={p} className="underline underline-offset-2 hover:text-primary" target="_blank" rel="noreferrer">
            {p.replace(/^https?:\/\/(www\.)?/, '')}
          </a>
        ) : (
          <span key={i}>{p}</span>
        ),
      )}
    </>
  );
}

export function AgentChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages, busy]);

  async function send(text: string) {
    const q = text.trim();
    if (!q || busy) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', text: q }]);
    setBusy(true);
    try {
      const res = await fetch(AGENT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          jsonrpc: '2.0',
          id: Date.now(),
          method: 'message/send',
          params: { message: { role: 'user', messageId: crypto.randomUUID(), parts: [{ kind: 'text', text: q }] } },
        }),
      });
      const data = (await res.json()) as {
        result?: { parts?: { text?: string }[] };
        error?: { message?: string };
      };
      const reply =
        data.result?.parts?.map((p) => p.text ?? '').join('\n').trim() ||
        data.error?.message ||
        'Something went wrong — try inferrex.com/contact.';
      setMessages((m) => [...m, { role: 'agent', text: reply }]);
    } catch {
      setMessages((m) => [...m, { role: 'agent', text: 'The agent is unreachable right now — try inferrex.com/contact.' }]);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {open && (
        <div className="fixed bottom-36 right-4 z-50 flex max-h-[min(32rem,calc(100dvh-10rem))] w-96 max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-xl border border-border bg-card shadow-2xl">
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <div className="flex items-center gap-2">
              <Sparkles className="size-4 text-primary" aria-hidden="true" />
              <span className="text-sm font-semibold text-foreground">Ask Inferrex</span>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="inline-flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
              aria-label="Close chat"
            >
              <X className="size-4" aria-hidden="true" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-3">
            {messages.length === 0 && (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  Ask about the platform, check whether a system is in the Corpus, or submit an API source.
                  Answers come only from published content.
                </p>
                <div className="flex flex-wrap gap-2">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => void send(s)}
                      className="rounded-full border border-border px-3 py-1 text-xs text-muted-foreground transition-colors hover:border-primary hover:text-foreground"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div
                key={i}
                className={cn(
                  'max-w-[85%] whitespace-pre-wrap rounded-lg px-3 py-2 text-sm',
                  m.role === 'user'
                    ? 'ml-auto bg-primary text-primary-foreground'
                    : 'mr-auto bg-muted text-foreground',
                )}
              >
                <Linkified text={m.text} />
              </div>
            ))}
            {busy && (
              <div className="mr-auto rounded-lg bg-muted px-3 py-2 text-sm text-muted-foreground" aria-live="polite">
                Thinking…
              </div>
            )}
          </div>

          <form
            className="flex items-center gap-2 border-t border-border p-3"
            onSubmit={(e) => {
              e.preventDefault();
              void send(input);
            }}
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask the Inferrex agent…"
              maxLength={2000}
              className="min-w-0 flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label="Message the Inferrex agent"
            />
            <button
              type="submit"
              disabled={busy || !input.trim()}
              className="inline-flex size-9 shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground transition-opacity disabled:opacity-40"
              aria-label="Send"
            >
              <SendHorizonal className="size-4" aria-hidden="true" />
            </button>
          </form>
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-20 right-4 z-50 inline-flex size-12 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-transform hover:scale-105 motion-reduce:transition-none"
        aria-label={open ? 'Close the Inferrex agent chat' : 'Ask the Inferrex agent'}
        aria-expanded={open}
      >
        {open ? <X className="size-5" aria-hidden="true" /> : <Sparkles className="size-5" aria-hidden="true" />}
      </button>
    </>
  );
}
